// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { Typography } from "@insite/mobius/index";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { notificationHeaderActions } from "@rugby/App/Store/Reducers";
import Link, { LinkPresentationProps } from "@rugby/MobiusOverrides/Link";
import { constants } from "@rugby/XDL/constants";
import Close from "@rugby/XDL/Icons/Close";
import parse from "html-react-parser";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const enum fields {
    message = "message",
    showCloseButton = "showCloseButton",
    showLink = "showLink",
    link = "link",
    linkMessage = "linkMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.link]: LinkFieldValue;
        [fields.showLink]: boolean;
        [fields.linkMessage]: string;
        [fields.message]: string;
        [fields.showCloseButton]: boolean;
    };
}

interface HeaderNotificationStyle {
    container?: InjectableCss;
    message?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    close?: IconPresentationProps;
}

export const headerNotificationStyle: HeaderNotificationStyle = {
    container: {
        css: css`
            background-color: ${getColor("common.ui3")};
            padding: 12px 26px;
            text-align: center;
            height: ${constants.header.notification.height}px;
            display: flex;
            justify-content: center;
            position: relative;
        `,
    },
    message: {
        size: 15,
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            display: flex;
            align-items: center;
        `,
    },
    link: {
        typographyProps: {
            size: 15,
            css: css`
                font-weight: bold;
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
        css: css`
            margin-left: 8px;
        `,
    },
    close: {
        src: Close,
        color: "secondary",
        size: 12,
        css: css`
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            cursor: pointer;
            &:hover {
                color: ${getColor("text.main")};
            }
        `,
    },
};

const styles = headerNotificationStyle;

const mapDispatchToProps = (dispatch: Dispatch<notificationHeaderActions>) => ({
    setNotificationHeaderState: (isHidden: boolean) =>
        dispatch({ type: "Header/isNotificationHeaderHidden", isNotificationHeaderHidden: isHidden }),
});

type Props = OwnProps & ReturnType<typeof mapDispatchToProps>;

const HeaderNotification: FC<Props> = ({ fields, setNotificationHeaderState }: Props) => {
    const [isHidden, setHidden] = useState(false);

    useEffect(() => {
        const isHiddenFromCookiesString = getCookie(constants.cookies.hiddenNotificationHeader);
        const isHiddenFromCookies = isHiddenFromCookiesString ? isHiddenFromCookiesString === "true" : false;

        setNotificationHeaderState(isHiddenFromCookies);
        setHidden(isHiddenFromCookies);
    }, []);

    const closeNotification = () => {
        setHidden(true);
        setCookie(constants.cookies.hiddenNotificationHeader, "true");
        setNotificationHeaderState(true);
    };

    if (isHidden || (!fields.message && !fields.link)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="header_notification">
            <Typography {...styles.message} data-test-selector="header_notification_message">
                {parse(fields.message || "")}
            </Typography>
            {fields.showLink && fields.link && (
                <Link
                    withIcon
                    {...styles.link}
                    href={fields.link.value || ""}
                    data-test-selector="header_notification_close"
                >
                    {fields.linkMessage}
                </Link>
            )}
            {fields.showCloseButton && <Icon {...styles.close} onClick={closeNotification} />}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(HeaderNotification),

    definition: {
        displayName: "Rugby Header Notification",
        group: "Xngage Custom" as any,
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.message,
                displayName: "Message",
                editorTemplate: "RichTextField",
                extendedConfig: { height: 180 },
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: false,
            },
            {
                name: fields.showLink,
                displayName: "Add link",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.linkMessage,
                displayName: "Link Message",
                editorTemplate: "TextField",
                defaultValue: "Join Our Team",
                fieldType: "General",
                isRequired: true,
                isVisible: item => item.fields.showLink,
            },
            {
                name: fields.link,
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: {
                    value: "",
                    type: "Url",
                },
                isRequired: true,
                isVisible: item => item.fields.showLink,
            },
            {
                name: fields.showCloseButton,
                displayName: "Show close button",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
